import ComponentName from "../constant/ComponentName";
import {
  CustomFilter,
  CustomFilterCategorical,
  CustomFilterDateSearch,
  CustomFilterField,
  CustomFilterFields,
  CustomFilterLiteralSearch
} from "../domain/ui/FilterDomains";
import {AuditLogType} from "../domain/rbac/RbacDomains";
import DataType from "../constant/DataType";
import VariableType from "../constant/VariableType";
import GatewayContainer from "../config/GatewayContainer";
import GatewayNames from "../constant/GatewayNames";
import TransactionClient from "../client/transaction/TransactionClient";
import {
  TransactionJsonModel,
  TransactionReason,
  TransactionResultReason,
  TransactionVariableJsonModel
} from "../domain/stream/transaction/TransactionDomains";
import CollectionNames from "../constant/CollectionNames";
import ValueFactory from "./ValueFactory";
import {dispatch, store} from "../redux/store";
import {query, repository} from "@trlogic/client-js";
import {InstanceContainer} from "../config/binding/InstanceBinding";
import Injectables from "../constant/Injectables";
import {changePageFilter, changePageFilters} from "../redux/actions/PageActions";
import moment from "moment/moment";
import {AchStages, DeploymentStatus, WatchlistType} from "../constant/Constants";
import {UserClient} from "../client/user/UserClient";
import RuleClient from "../client/rule/RuleClient";
import {ListColumnField} from "../domain/list/ListDomains";
import LanguageUtil from "./LanguageUtil";
import {AlertStatus} from "../domain/watchlist/WatchlistAlertDomains";
import {RoleClient} from "../client/role/RoleClient";
import {GroupClient} from "../client/group/GroupClient";
import StructClient from "../client/struct/StructClient";
import {DataStructure} from "../domain/structure/DataStructureDomains";
import {ArrayVariableProperties, CustomStructureProperties, ObjectVariableProperties} from "../domain/datatype/VariablePropertiesDomains";
import {KeycloakLoginTypes, KeycloakOperationType, KeycloakResourceTypes} from "../domain/keycloak/KeycloakEventTypes";
import {PageState} from "../redux/reducers/PageReducer";
import { CraSegmentType } from '../domain/cra/CraDomains';
import QueryBuilder = query.QueryBuilder;
import QueryableRepository = repository.QueryableRepository;
import Filter = query.Filter;
import eq = query.eq;
import neq = query.neq;
import gt = query.gt;
import gte = query.gte;
import lt = query.lt;
import lte = query.lte;
import empty = query.empty;
import or = query.or;
import and = query.and;
import nor = query.nor;
import includes = query.includes;
import starts = query.starts;
import ends = query.ends;
import exists = query.exists;
import regex = query.regex;

class FilterUtil {

  public static readonly exportAuditLogTypes: AuditLogType[] = [
    AuditLogType.CASE_DATA_EXPORT,
    AuditLogType.TRANSACTION_DATA_EXPORT,
    AuditLogType.AUDIT_LOG_EXPORT,
    AuditLogType.CASE_LOG_EXPORT,
    AuditLogType.ALERT_EXPORT,
    AuditLogType.LIST_DATA_EXPORT,
    AuditLogType.CASE_VARIABLE_PANEL_EXPORT,
    AuditLogType.CHART_EXPORT,
    AuditLogType.GRAPH_EXPORT,
    AuditLogType.ADMIN_AUDITS_EXPORT,
    AuditLogType.LOGIN_AUDITS_EXPORT,
    AuditLogType.CRA_RESULT_EXPORT,
    AuditLogType.MATCH_MANY_LOG_EXPORT,
    AuditLogType.MATCH_MANY_LOG_RESULT_EXPORT,
    AuditLogType.ACH_DATA_EXPORT,
  ];

  public static async getFields(component: ComponentName): Promise<CustomFilterFields[]> {
    switch (component) {
      case ComponentName.TRANSACTION:
        return this.getTransactionFields();
      case ComponentName.STREAM:
        return this.getStreamFields();
      case ComponentName.LIST:
        return this.getListFields();
      case ComponentName.ENRICHMENT:
        return this.getEnrichmentFields();
      case ComponentName.RULE:
        return this.getRuleFields()
      case ComponentName.CONNECTION:
        return this.getConnectionFields();
      case ComponentName.STRUCT:
        return this.getStructFields();
      case ComponentName.ACTION:
        return this.getActionFields();
      case ComponentName.CONFIGURATION:
        return this.getConfigurationFields();
      case ComponentName.TRACKER:
        return this.getTrackerFields();
      case ComponentName.ADMIN_USER:
        return this.getUserFields();
      case ComponentName.ADMIN_GROUP:
        return this.getGroupFields();
      case ComponentName.ADMIN_ROLE:
        return this.getRoleFields();
      case ComponentName.DASHBOARD:
        return this.getDashboardFields();
      case ComponentName.ADMIN_AUDIT_LOG:
        return this.getAdminAuditFields();
      case ComponentName.LOGIN_AUDIT_LOG:
        return this.getLoginAuditFields();
      case ComponentName.COMPONENT_AUDIT_LOG:
        return this.getAuditFields();
      case ComponentName.EXPORT_IMPORT_AUDIT_LOG:
        return this.getExportImportAuditFields();
      case ComponentName.ASSESSMENT:
        return this.getAssessmentFields();
      case ComponentName.ACH:
        return this.getAchFields();
      case ComponentName.AI_MODEL:
        return this.getAiModelFields();
      case ComponentName.CUSTOM_LIST:
        return this.getCustomListFields();
      default:
        return []
    }
  }

  public static async getCraSegmentFields(segment: CraSegmentType): Promise<CustomFilterFields[]> {
    switch (segment) {
      case CraSegmentType.STRING:
        return [
          {
            type: "LITERAL",
            key: "value",
            label: LanguageUtil.get({id:"common.label.value", defaultMessage:"Value"}),
            properties: {dataType: DataType.STRING}
          },
          {
            type: "LITERAL",
            key: "score",
            label: LanguageUtil.get({id:"common.label.score", defaultMessage:"Score"}),
            properties: {dataType: DataType.NUMBER}
          }
        ]
      case CraSegmentType.DATE:
      case CraSegmentType.NUMBER:
        return [
          {
            type: "LITERAL",
            key: "from",
            label: LanguageUtil.get({id:"common.label.from", defaultMessage:"From"}),
            properties: {dataType: DataType.NUMBER}
          },
          {
            type: "LITERAL",
            key: "to",
            label: LanguageUtil.get({ id: "common.label.to", defaultMessage: "To" }),
            properties: { dataType: DataType.NUMBER }
          },
          {
            type: "LITERAL",
            key: "score",
            label: LanguageUtil.get({id:"common.label.score", defaultMessage:"Score"}),
            properties: {dataType: DataType.NUMBER}
          }
        ]
      default:
        return []

    }

  }

  public static getDashboardFields(): CustomFilterFields[] {
    return [
      {
        type: "LITERAL",
        key: "name",
        label: LanguageUtil.get({id:"common.label.name", defaultMessage:"Name"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        key: "description",
        label: LanguageUtil.get({id:"common.label.description", defaultMessage:"Description"}),
        properties: {dataType: DataType.STRING}
      },
      this.getDeployStatus(),
    ]
  }

  private static async getAiModelFields(): Promise<CustomFilterFields[]> {
  
    const stageItems : Array<{ label: string; key: DeploymentStatus }> = [
      {
        label: LanguageUtil.get({id:"common.label.draft", defaultMessage:"Draft"}),
        key: "DRAFT" as DeploymentStatus
      },
      {
        label: LanguageUtil.get({id:"common.label.active", defaultMessage:"Active"}),
        key: "ACTIVE" as DeploymentStatus
      },
      {
        label: LanguageUtil.get({id:"common.label.scheduled", defaultMessage:"Scheduled"}),
        key: "SCHEDULED" as DeploymentStatus
      },
      {
        label: LanguageUtil.get({id:"common.label.inactive", defaultMessage:"Inactive"}),
        key: "INACTIVE" as DeploymentStatus
      },
      {
        label: LanguageUtil.get({id:"common.label.expired", defaultMessage:"Expired"}),
        key: "EXPIRED" as DeploymentStatus
      },
      {
        label: LanguageUtil.get({id:"common.label.failed", defaultMessage:"Failed"}),
        key: "FAILED" as DeploymentStatus
      },
      {
        label: LanguageUtil.get({id:"common.label.restoring", defaultMessage:"Restoring"}),
        key: "RESTORING" as DeploymentStatus
      },
      {
        label: LanguageUtil.get({id:"common.label.rebalancing", defaultMessage:"Rebalancing"}),
        key: "REBALANCING" as DeploymentStatus
      }
    ];
    return [
      {
        type: "LITERAL",
        key: "name",
        label: LanguageUtil.get({id:"common.label.name", defaultMessage:"Name"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: "stage",
        label: LanguageUtil.get({id:"common.label.status", defaultMessage:"Status"}),
        properties: {dataType: DataType.STRING},
        items: stageItems
      },
      {
        type: "LITERAL",
        key: "description",
        label: LanguageUtil.get({id:"common.label.description", defaultMessage:"Description"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "DATE",
        key: "createdAt",
        label: LanguageUtil.get({id:"common.label.createdAt", defaultMessage:"Created At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: "updatedAt",
        label: LanguageUtil.get({id:"common.label.updatedAt", defaultMessage:"Updated At"}),
        properties: {dataType: DataType.DATE}
      }
    ]
  }

  private static async getAchFields(): Promise<CustomFilterFields[]> {
    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const users: { username: string; fullName: string }[] = await userClient.getUsernames();
    const userItems = users.map((user) => ({
      label: user.fullName,
      key: user.username
    }));

    const stageItems: Array<{ label: string; key: AchStages }> = [
      {
        label: LanguageUtil.get({id: "pages.ach.stage.done", defaultMessage: "Done"}),
        key: "DONE"
      },
      {
        label: LanguageUtil.get({id: "pages.ach.stage.inProgress", defaultMessage: "In Progress"}),
        key: "IN_PROGRESS"
      },
      {
        label: LanguageUtil.get({id: "pages.ach.stage.failed", defaultMessage: "Failed"}),
        key: "FAILED"
      },
      {
        label: LanguageUtil.get({id: "pages.ach.stage.scheduled", defaultMessage: "Scheduled"}),
        key: "SCHEDULED"
      }
    ];

    return [
      {
        type: "LITERAL",
        key: "fileName",
        label: LanguageUtil.get({id: "pages.ach.table.achFileName", defaultMessage: "ACH File Name"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: "stage",
        label: LanguageUtil.get({id: "pages.ach.table.status", defaultMessage: "Status"}),
        properties: {dataType: DataType.STRING},
        items: stageItems
      },
      {
        type: "CATEGORICAL",
        key: "uploader",
        label: LanguageUtil.get({id: "pages.ach.table.uploader", defaultMessage: "Uploader"}),
        properties: {dataType: DataType.STRING},
        items: userItems
      },
      {
        type: "DATE",
        key: "createdAt",
        label: LanguageUtil.get({id: "pages.ach.table.createdAt", defaultMessage: "Created At"}),
        properties: {dataType: DataType.DATE}
      }
    ];
  }

  public static async getLoginAuditFields(): Promise<CustomFilterFields[]> {
    const handleLoginType = (type: string) => {
      switch (type) {
        case "LOGIN":
          return LanguageUtil.get({id:"messages.success.successLogin", defaultMessage:"Successful Login"});
        case "LOGOUT":
          return LanguageUtil.get({id:"messages.success.successLogout", defaultMessage:"Successful Logout"});
        case "LOGIN_ERROR":
          return LanguageUtil.get({id:"messages.error.failedLogin", defaultMessage:"Failed Login"});
        case "LOGOUT_ERROR":
          return LanguageUtil.get({id:"messages.error.failedLogout", defaultMessage:"Failed Logout"});
        default:
          return type;
      }
    }
    const keycloakLoginTypesItems = ["LOGIN", "LOGIN_ERROR", "LOGOUT", "LOGOUT_ERROR"].map((key) => {
      return {
        label: handleLoginType(key),
        key: KeycloakLoginTypes[key as keyof typeof KeycloakLoginTypes]
      }
    })
    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const users: { username: string, fullName: string }[] = await userClient.getUsernames();
    const userItems = users.map((user) => {
      return {
        label: user.fullName,
        key: user.username
      }
    })
    return [
      {
        type: "CATEGORICAL",
        key: "user_id",
        label: LanguageUtil.get({id:"common.label.username", defaultMessage:"Username"}),
        items: userItems,
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: "type",
        label: LanguageUtil.get({id:"common.label.action", defaultMessage:"Action"}),
        properties: {dataType: DataType.STRING},
        items: [...keycloakLoginTypesItems]
      },
      {
        type: "DATE",
        key: "event_time",
        label: LanguageUtil.get({id:"common.label.date", defaultMessage:"Date"}),
        properties: {dataType: DataType.DATE},
      },
    ]
  }

  public static async getAdminAuditFields(): Promise<CustomFilterFields[]> {
    const keycloakOperationTypes = Object.keys(KeycloakOperationType).map((key) => {
      return {
        label: key.split("_").join(" "),
        key: KeycloakOperationType[key as keyof typeof KeycloakOperationType]
      }
    });
    const keycloakResourceTypes = Object.keys(KeycloakResourceTypes).map((key) => {
      return {
        label: key.split("_").join(" "),
        key: KeycloakResourceTypes[key as keyof typeof KeycloakResourceTypes]
      }
    });
    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const users: { username: string, fullName: string }[] = await userClient.getUsernames();
    const userItems = users.map((user) => {
      return {
        label: user.fullName,
        key: user.username
      }
    })
    const fields: CustomFilterFields[] = [
      {
        type: "CATEGORICAL",
        key: "username.keyword",
        label: LanguageUtil.get({id:"common.label.username", defaultMessage:"Username"}),
        items: userItems,
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: "operation.keyword",
        label: LanguageUtil.get({id:"common.label.action", defaultMessage:"Action"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label:  LanguageUtil.get({id:"common.label.create", defaultMessage:"Create"}),
            key: "CREATE"
          },
          {
            label: LanguageUtil.get({id:"common.label.update", defaultMessage:"Update"}),
            key: "UPDATE"
          },
          {
            label: LanguageUtil.get({id:"common.label.delete", defaultMessage:"Delete"}),
            key: "DELETE"
          }
        ]
      },
      {
        type: "CATEGORICAL",
        key: "component.keyword",
        label: LanguageUtil.get({id:"common.label.type", defaultMessage:"Type"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.user", defaultMessage:"User"}),
            key: ComponentName.ADMIN_USER
          },
          {
            label: LanguageUtil.get({id:"common.label.group", defaultMessage:"Group"}),
            key: ComponentName.ADMIN_GROUP
          },
          {
            label: LanguageUtil.get({id:"common.label.role", defaultMessage:"Role"}),
            key: ComponentName.ADMIN_ROLE
          }
        ]
      },
      {
        type: "LITERAL",
        properties: {dataType: DataType.STRING},
        label: LanguageUtil.get({id:"common.label.resourceId", defaultMessage:"Resource Id"}),
        key: "document.id.keyword"
      },
      {
        type: "DATE",
        key: "timestamp",
        label: LanguageUtil.get({id:"common.label.date", defaultMessage:"Date"}),
        properties: {dataType: DataType.DATE},
      }
    ]
    return fields;
  }

  public static async getExportImportAuditFields(): Promise<CustomFilterFields[]> {
    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const users: { username: string, fullName: string }[] = await userClient.getUsernames();
    const userItems = users.map((user) => {
      return {
        label: user.fullName,
        key: user.username
      }
    })
    const fields: CustomFilterFields[] = [
      {
        type: "CATEGORICAL",
        key: "username.keyword",
        label: LanguageUtil.get({id:"common.label.username", defaultMessage:"Username"}),
        items: userItems,
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: "operation.keyword",
        label: LanguageUtil.get({id:"common.label.operation", defaultMessage:"Operation"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.export", defaultMessage:"Export"}),
            key: "EXPORT"
          },
          {
            label: LanguageUtil.get({id:"common.label.import", defaultMessage:"Import"}),
            key: "IMPORT"
          }
        ]
      },
      {
        type: "CATEGORICAL",
        key: "component.keyword",
        label: LanguageUtil.get({id:"common.label.component", defaultMessage:"Component"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.channel", defaultMessage:"Channel"}),
            key: ComponentName.TRANSACTION
          },
          {
            label: LanguageUtil.get({id:"common.label.store", defaultMessage:"Store"}),
            key: ComponentName.STREAM
          },
          {
            label: LanguageUtil.get({id:"common.label.list", defaultMessage:"List"}),
            key: ComponentName.LIST
          },
          {
            label: LanguageUtil.get({id:"common.label.enrichment", defaultMessage:"Enrichment"}),
            key: ComponentName.ENRICHMENT
          },
          {
            label: LanguageUtil.get({id:"common.label.rule", defaultMessage:"Rule"}),
            key: ComponentName.RULE
          },
          {
            label: LanguageUtil.get({id:"common.label.connection", defaultMessage:"Connection"}),
            key: ComponentName.CONNECTION
          },
          {
            label: LanguageUtil.get({id:"common.label.struct", defaultMessage:"Struct"}),
            key: ComponentName.STRUCT
          },
          {
            label: LanguageUtil.get({id:"common.label.mlopsU", defaultMessage:"ML-OPS"}),
            key: ComponentName.AI_MODEL
          },
          {
            label: LanguageUtil.get({id:"common.label.tracker", defaultMessage:"Tracker"}),
            key: ComponentName.TRACKER
          },
          {
            label: LanguageUtil.get({id:"common.label.action", defaultMessage:"Action"}),
            key: ComponentName.ACTION
          },
          {
            label: LanguageUtil.get({id:"common.label.configuration", defaultMessage:"Configuration"}),
            key: ComponentName.CONFIGURATION
          },
          {
            label: LanguageUtil.get({id:"common.label.transaction", defaultMessage:"Transaction"}),
            key: ComponentName.CASE
          },
          {
            label: LanguageUtil.get({id:"common.label.chart", defaultMessage:"Chart"}),
            key: ComponentName.CHART
          },
          {
            label: LanguageUtil.get({id:"common.label.graph", defaultMessage:"Graph"}),
            key: ComponentName.GRAPH
          },
          {
            label: LanguageUtil.get({id:"common.label.watchlistAlert", defaultMessage:"WatchlistAlert"}),
            key: ComponentName.WATCHLIST_ALERT
          },
          {
            label: LanguageUtil.get({id:"common.label.assessment", defaultMessage:"Assessment"}),
            key: ComponentName.ASSESSMENT
          },
          {
            label: LanguageUtil.get({id:"common.label.ach", defaultMessage:"ACH"}),
            key: ComponentName.ACH
          },
          {
            label: LanguageUtil.get({id:"common.label.customList", defaultMessage:"Custom List"}),
            key: ComponentName.CUSTOM_LIST
          },
          {
            label: LanguageUtil.get({id:"common.label.adminAuditLog", defaultMessage:"Admin Audit Log"}),
            key: ComponentName.ADMIN_AUDIT_LOG
          },
          {
            label: LanguageUtil.get({id:"common.label.loginAuditLog", defaultMessage:"Login Audit Log"}),
            key: ComponentName.LOGIN_AUDIT_LOG
          }
        ]
      },
      {
        type: "LITERAL",
        properties: {dataType: DataType.STRING},
        label: LanguageUtil.get({id:"common.label.protocol", defaultMessage:"Protocol"}),
        key: "protocol"
      },
      {
        type: "LITERAL",
        properties: {dataType: DataType.NUMBER},
        label: LanguageUtil.get({id:"common.label.port", defaultMessage:"Port"}),
        key: "port"
      },
      {
        type: "DATE",
        key: "timestamp",
        label: LanguageUtil.get({id:"common.label.date", defaultMessage:"Date"}),
        properties: {dataType: DataType.DATE},
      }
    ]
    return fields;
  }

  public static async getAuditFields(): Promise<CustomFilterFields[]> {
    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const users: { username: string, fullName: string }[] = await userClient.getUsernames();
    const userItems = users.map((user) => {
      return {
        label: user.fullName,
        key: user.username
      }
    })
    const fields: CustomFilterFields[] = [
      {
        type: "CATEGORICAL",
        key: "username",
        label: LanguageUtil.get({id:"common.label.username", defaultMessage:"Username"}),
        items: userItems,
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: "operation",
        label: LanguageUtil.get({id:"common.label.operation", defaultMessage:"Operation"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.create", defaultMessage:"Create"}),
            key: "CREATE"
          },
          {
            label: LanguageUtil.get({id:"common.label.update", defaultMessage:"Update"}),
            key: "UPDATE"
          },
          {
            label: LanguageUtil.get({id:"common.label.delete", defaultMessage:"Delete"}),
            key: "DELETE"
          },
          {
            label: LanguageUtil.get({id:"common.label.deploy", defaultMessage:"Deploy"}),
            key: "DEPLOY",
          },
          {
            label: LanguageUtil.get({id:"common.label.undeploy", defaultMessage:"Undeploy"}),
            key: "UNDEPLOY"
          },
          {
            label: LanguageUtil.get({id:"common.label.import", defaultMessage:"Import"}),
            key: "IMPORT"
          }
        ]
      },
      {
        type: "DATE",
        key: "timestamp",
        label: LanguageUtil.get({id:"common.label.timestamp", defaultMessage:"Timestamp"}),
        properties: {dataType: DataType.DATE},
      },
      {
        type: "CATEGORICAL",
        key: "component",
        label: LanguageUtil.get({id:"common.label.component", defaultMessage:"Component"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.dahsboard", defaultMessage:"Dashboard"}),
            key: ComponentName.DASHBOARD,
          },
          {
            label: LanguageUtil.get({id:"common.label.transaction", defaultMessage:"Transaction"}),
            key: ComponentName.CASE,
          },
          {
            label: LanguageUtil.get({id:"common.label.entity", defaultMessage:"Entity"}),
            key: ComponentName.ENTITY,
          },
          {
            label: LanguageUtil.get({id:"common.label.graph", defaultMessage:"Graph"}),
            key: ComponentName.GRAPH
          },
          {
            label: LanguageUtil.get({id:"common.label.configuration", defaultMessage:"Configuration"}),
            key: ComponentName.CONFIGURATION
          },
          {
            label: LanguageUtil.get({id:"common.label.watchlist", defaultMessage:"Watchlist"}),
            key: ComponentName.WATCHLIST,
          },
          {
            label: LanguageUtil.get({id:"common.label.watchlistAlert", defaultMessage:"WatchlistAlert"}),
            key: ComponentName.WATCHLIST_ALERT,
          },
          {
            label: LanguageUtil.get({id:"common.label.channel", defaultMessage:"Channel"}),
            key: ComponentName.TRANSACTION
          },
          {
            label: LanguageUtil.get({id:"common.label.store", defaultMessage:"Store"}),
            key: ComponentName.STREAM
          },
          {
            label: LanguageUtil.get({id:"common.label.list", defaultMessage:"List"}),
            key: ComponentName.LIST
          },
          {
            label: LanguageUtil.get({id:"common.label.enrichment", defaultMessage:"Enrichment"}),
            key: ComponentName.ENRICHMENT
          },
          {
            label: LanguageUtil.get({id:"common.label.rule", defaultMessage:"Rule"}),
            key: ComponentName.RULE
          }, {
            label: LanguageUtil.get({id:"common.label.connection", defaultMessage:"Connection"}),
            key: ComponentName.CONNECTION
          },
          {
            label: LanguageUtil.get({id:"common.label.struct", defaultMessage:"Struct"}),
            key: ComponentName.STRUCT
          },
          {
            label: LanguageUtil.get({id:"common.label.mlopsU", defaultMessage:"ML-OPS"}),
            key: ComponentName.AI_MODEL,
          },
          {
            label: LanguageUtil.get({id:"common.label.tracker", defaultMessage:"Tracker"}),
            key: ComponentName.TRACKER,
          },
          {
            label: LanguageUtil.get({id:"common.label.action", defaultMessage:"Action"}),
            key: ComponentName.ACTION
          }
        ]
      },
      {
        type: "LITERAL",
        properties: {dataType: DataType.STRING},
        label: LanguageUtil.get({id:"common.label.componentName", defaultMessage:"Component Name"}),
        key: "document.id"
      }
    ]
    return fields;
  }

  public static getCustomListFields(): CustomFilterFields[] {
    return [
      {
        type: 'LITERAL',
        key: 'name',
        label: LanguageUtil.get({id: 'pages.customList.table.listName', defaultMessage: 'List Name'}),
        properties: {dataType: DataType.STRING},
      },
      {
        type: 'LITERAL',
        key: 'description',
        label: LanguageUtil.get({id: 'pages.customList.table.description', defaultMessage: 'Description'}),
        properties: {dataType: DataType.STRING},
      },
      {
        type: 'DATE',
        key: 'createdAt',
        label: LanguageUtil.get({id: 'pages.customList.table.createdAt', defaultMessage: 'Created At'}),
        properties: {dataType: DataType.DATE},
      },
      {
        type: 'DATE',
        key: 'updatedAt',
        label: LanguageUtil.get({id: 'pages.customList.table.updatedAt', defaultMessage: 'Updated At'}),
        properties: {dataType: DataType.DATE},
      },
    ];
  }

  public static getWatchlistHistoryFields(): CustomFilterFields[] {
    return [
      {
        type: 'LITERAL',
        key: 'referenceId',
        label: LanguageUtil.get({id: 'common.label.referenceId', defaultMessage: 'Reference ID'}),
        properties: {dataType: DataType.STRING},
      },
      {
        type: 'CATEGORICAL',
        key: 'matchType',
        label: LanguageUtil.get({id: 'common.label.type', defaultMessage: 'Type'}),
        properties: {dataType: DataType.STRING},
        items: [
          {label: LanguageUtil.get({id: 'common.tabs.individual', defaultMessage: 'Individual'}), key: 'INDIVIDUAL'},
          {label: LanguageUtil.get({id: 'common.tabs.entity', defaultMessage: 'Entity'}), key: 'ENTITY'},
          {label: LanguageUtil.get({id: 'common.tabs.general', defaultMessage: 'General'}), key: 'GENERAL'},
        ],
      },
      {
        type: 'LITERAL',
        key: 'searchedName',
        label: LanguageUtil.get({id: 'pages.watchlist.history.searchedName', defaultMessage: 'Searched Name'}),
        properties: {dataType: DataType.STRING},
      },
      {
        type: 'LITERAL',
        key: 'threshold',
        label: LanguageUtil.get({id: 'pages.watchlist.history.threshold', defaultMessage: 'Threshold'}),
        properties: {dataType: DataType.NUMBER},
      },
      {
        type: 'LITERAL',
        key: 'maxScore',
        label: LanguageUtil.get({id: 'pages.watchlist.history.maxScore', defaultMessage: 'Max Score'}),
        properties: {dataType: DataType.NUMBER},
      },
      {
        type: 'LITERAL',
        key: 'resultCount',
        label: LanguageUtil.get({id: 'pages.watchlist.history.resultCount', defaultMessage: 'Result Count'}),
        properties: {dataType: DataType.NUMBER},
      },
      {
        type: 'LITERAL',
        key: 'loggedCount',
        label: LanguageUtil.get({id: 'pages.watchlist.history.loggedCount', defaultMessage: 'Logged Count'}),
        properties: {dataType: DataType.NUMBER},
      },
      {
        type: 'CATEGORICAL',
        key: 'truncated',
        label: LanguageUtil.get({id: 'pages.watchlist.history.truncated', defaultMessage: 'Truncated'}),
        properties: {dataType: DataType.BOOLEAN},
        items: [
          {label: LanguageUtil.get({id: 'common.label.yes', defaultMessage: 'Yes'}), key: true},
          {label: LanguageUtil.get({id: 'common.label.no', defaultMessage: 'No'}), key: false},
        ],
      },
      {
        type: 'DATE',
        key: 'createdAt',
        label: LanguageUtil.get({id: 'common.label.createdAt', defaultMessage: 'Created At'}),
        properties: {dataType: DataType.DATE},
      },
    ];
  }

  public static getCustomListRecordFields(recordType: 'individual' | 'entity'): CustomFilterFields[] {
    const fields: CustomFilterFields[] = [
      {
        type: 'LITERAL',
        key: 'name',
        label: LanguageUtil.get({id: 'common.label.name', defaultMessage: 'Name'}),
        properties: {dataType: DataType.STRING},
      },
      {
        type: 'DATE',
        key: 'createdAt',
        label: LanguageUtil.get({id: 'pages.customList.table.createdAt', defaultMessage: 'Created At'}),
        properties: {dataType: DataType.DATE},
      },
    ];

    if (recordType === 'individual') {
      fields.splice(1, 0, {
        type: 'CATEGORICAL',
        key: 'gender',
        label: LanguageUtil.get({id: 'pages.customList.table.gender', defaultMessage: 'Gender'}),
        properties: {dataType: DataType.STRING},
        items: [
          {label: LanguageUtil.get({id: 'common.label.male', defaultMessage: 'Male'}), key: 'MALE'},
          {label: LanguageUtil.get({id: 'common.label.female', defaultMessage: 'Female'}), key: 'FEMALE'},
          {label: LanguageUtil.get({id: 'common.label.unknown', defaultMessage: 'Unknown'}), key: 'UNKNOWN'},
        ],
      });
    }

    return fields;
  }

  public static async getWatchlistAlertFields(type: WatchlistType) {
    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const users: { username: string, fullName: string }[] = await userClient.getUsernames();
    const fields: CustomFilterFields[] = [
      {
        type: "LITERAL",
        key: "score",
        label: LanguageUtil.get({id:"common.label.score", defaultMessage:"Score"}),
        properties: {dataType: DataType.NUMBER}
      },
      {
        type: "LITERAL",
        key: "request.name",
        label: LanguageUtil.get({id:"common.label.name", defaultMessage:"Name"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        key: "metadata.reason",
        label: LanguageUtil.get({id:"common.label.reason", defaultMessage:"Reason"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        key: "results.level",
        label: "Level",
        properties: {dataType: DataType.NUMBER}
      },
      {
        type: "CATEGORICAL",
        key: "metadata.assignee.keyword",
        label: LanguageUtil.get({id:"common.label.assignee", defaultMessage:"Assignee"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.system", defaultMessage:"System"}),
            key: "System"
          },
          ...users.map((user: { username: string, fullName: string }) => ({
            label: user.fullName,
            key: `${user.username}`
          }))
        ]
      },
      {
        type: "CATEGORICAL",
        key: "results.record.records.watchlistRecord.categories",
        label: "Category",
        properties: {dataType: DataType.ARRAY},
        items: [
          {
            label: "SIP",
            key: "SIP"
          },
          {
            label: "SIE",
            key: "SIE"
          },
          {
            label: "TER",
            key: "TER"
          },
          {
            label: "CRI",
            key: "CRI"
          },
          {
            label: "PEP",
            key: "PEP"
          }
        ]
      },
      {
        type: "CATEGORICAL",
        key: "status.keyword",
        label: LanguageUtil.get({id:"common.label.status", defaultMessage:"Status"}),
        properties: {dataType: DataType.STRING},
        items: Object.values(AlertStatus).map((reason) => ({
          label: reason.replaceAll("_", " "),
          key: reason
        }))
      },
      {
        type: "DATE",
        key: "results.timestamp",
        label: LanguageUtil.get({id:"common.label.createdAt", defaultMessage:"Create At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: "updatedAt",
        label: LanguageUtil.get({id:"common.label.updatedAt", defaultMessage:"Updated At"}),
        properties: {dataType: DataType.DATE}
      }
    ];
    if (type == "Individual") {
      fields.splice(2, 0,
        {
          type: "LITERAL",
          key: "request.identification",
          label: LanguageUtil.get({id:"common.label.identificationU", defaultMessage:"Identification"}),
          properties: {dataType: DataType.STRING}
        },
        {
          type: "LITERAL",
          key: "request.citizenship",
          label: LanguageUtil.get({id:"common.label.citizenship", defaultMessage:"Citizenship"}),
          properties: {dataType: DataType.STRING}
        },
        {
          type: "LITERAL",
          key: "request.dateOfBirth",
          label: LanguageUtil.get({id:"common.label.dateOfBirth", defaultMessage:"Date of Birth"}),
          properties: {dataType: DataType.STRING}
        }
      )
    } else if (type == "Entity") {
      fields.splice(2, 0,
        {
          type: "LITERAL",
          key: "request.identifier",
          label: LanguageUtil.get({id:"common.label.identifierU", defaultMessage:"Identifier"}),
          properties: {dataType: DataType.STRING}
        },
        {
          type: "LITERAL",
          key: "request.dateOfRegistration",
          label: LanguageUtil.get({id:"common.label.dateOfRegistration", defaultMessage:"Date of Registration"}),
          properties: {dataType: DataType.STRING}
        }
      )
    } else if (type == "General") {
      fields.splice(2, 0,
        {
          type: "LITERAL",
          key: "request.identification",
          label: "Identification",
          properties: {dataType: DataType.STRING}
        },
        {
          type: "LITERAL",
          key: "request.identifier",
          label: "Identifier",
          properties: {dataType: DataType.STRING}
        },
        {
          type: "LITERAL",
          key: "request.dateOfBirth",
          label: "Date Of Birth",
          properties: {dataType: DataType.STRING}
        },
        {
          type: "LITERAL",
          key: "request.dateOfRegistration",
          label: "Date Of Registration",
          properties: {dataType: DataType.STRING}
        }
      )
    }
    return fields;
  }

  public static async getEntityFields(transactionName: string | undefined, variables: string[], saveFilter: boolean = false) {
    const page = store.getState().page;
    const repository: QueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const users: { username: string, fullName: string }[] = await userClient.getUsernames();
    const fields: CustomFilterFields[] = [
      {
        type: "LITERAL",
        key: "id.keyword",
        label: LanguageUtil.get({id:"common.label.id", defaultMessage:"ID"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: `metadata.users`,
        label: LanguageUtil.get({id:"common.label.users", defaultMessage:"Users"}),
        properties: {dataType: DataType.STRING},
        items: [
          ...users.map((user: { username: string, fullName: string }) => ({
            label: user.fullName,
            key: `${user.username}`
          }))
        ]
      },
      {
        type: "DATE",
        key: "updatedAt",
        label: LanguageUtil.get({id:"common.label.updateAt", defaultMessage:"Update At"}),
        properties: {dataType: DataType.DATE}
      },
    ];

    if (transactionName != undefined) {
      const transactionQuery = QueryBuilder.select()
        .from(CollectionNames.TRANSACTIONS)
        .where(eq("id", transactionName))
        .build();

      const transaction = await repository.find<TransactionJsonModel>(transactionQuery);
      if (transaction != undefined) {
        for (const v1 of transaction.variables
          .sort((a, b) => a.name.localeCompare(b.name))
          .filter(v => variables.includes(v.name) && v.properties.dataType != DataType.STRUCTURE)) {
          await this.addVariable(v1, fields);
        }
      }
    }

    dispatch(changePageFilter({
      filter: saveFilter ? page.filter.filter : ValueFactory.initializeFilter(),
      items: fields
    }));
  }

  public static async getCaseFields(transactionName: string | undefined, saveFilter: boolean = false, key?: string) {
    const repository: QueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
    const userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);
    const ruleClient: RuleClient = GatewayContainer.getInstance().get(GatewayNames.RULE_CLIENT);
    const page = store.getState().page as PageState;
    const user = store.getState().auth.user;
    if (user == undefined) {
      return;
    }
    const users: { username: string, fullName: string }[] = await userClient.getUsernames();
    const ruleNames: string[] = await ruleClient.getNames(transactionName ? eq("workflow.children.value.name", transactionName) : empty());
    const status = [
      TransactionReason.WAITING_FOR_REVIEW,
      TransactionReason.IN_REVIEW,
      TransactionReason.ON_HOLD,
    ]
    if (user.activeRiskType == "fraud") {
      status.push(TransactionReason.FRAUD)
      status.push(TransactionReason.NOT_FRAUD)
    } else {
      status.push(TransactionReason.COMPLIANT)
      status.push(TransactionReason.IN_COMPLIANT)
    }
    const fields: CustomFilterFields[] = [
      {
        type: "LITERAL",
        key: "id.keyword",
        label: LanguageUtil.get({id:"common.label.id", defaultMessage:"ID"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        key: `result.${user.activeRiskType}.score`,
        label: LanguageUtil.get({id:"common.label.score", defaultMessage:"Score"}),
        properties: {dataType: DataType.NUMBER}
      },
      {
        type: "CATEGORICAL",
        key: `result.${user.activeRiskType}.metadata.assignee.id.keyword`,
        label: LanguageUtil.get({id:"common.label.assignee", defaultMessage:"Assignee"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.system", defaultMessage:"System"}),
            key: "System"
          },
          ...users.map((user: { username: string, fullName: string }) => ({
            label: user.fullName,
            key: `${user.username}`
          }))
        ]
      },
      {
        type: "CATEGORICAL",
        key: `result.${user.activeRiskType}.rules.name.keyword`,
        label: LanguageUtil.get({id:"common.label.rule", defaultMessage:"Rule"}),
        properties: {dataType: DataType.STRING},
        items: ruleNames.map((ruleName) => ({
          label: ruleName,
          key: ruleName
        }))
      },
      {
        type: "CATEGORICAL",
        key: `result.${user.activeRiskType}.resolution.keyword`,
        label: LanguageUtil.get({id:"common.label.resolution", defaultMessage:"Resolution"}),
        properties: {dataType: DataType.STRING},
        items: Object.values(TransactionResultReason).map((reason) => ({
          label: reason,
          key: reason
        }))
      },
      {
        type: "CATEGORICAL",
        key: `result.${user.activeRiskType}.status.keyword`,
        label: LanguageUtil.get({id:"common.label.status", defaultMessage:"Status"}),
        properties: {dataType: DataType.STRING},
        items: Object.values(status).map((reason) => ({
          label: reason.replace(/_/g, " "),
          key: reason
        }))
      },
      {
        type: "CATEGORICAL",
        subType: "EXIST",
        label: LanguageUtil.get({id:"common.label.failures", defaultMessage:"Failures"}),
        properties: {dataType: DataType.BOOLEAN},
        key: "result.failures",
        items: [
          {
            label: LanguageUtil.get({id:"common.label.true", defaultMessage:"True"}),
            key: true
          },
          {
            label: LanguageUtil.get({id:"common.label.false", defaultMessage:"False"}),
            key: false,
          }
        ]
      },
      {
        type: "DATE",
        key: "createdAt",
        label:LanguageUtil.get({id:"common.label.createAt", defaultMessage:"Create At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: `result.${user.activeRiskType}.metadata.review.endDate`,
        label: LanguageUtil.get({id:"common.label.closedAt", defaultMessage:"Closed At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: "updatedAt",
        label: LanguageUtil.get({id:"common.label.updateAt", defaultMessage:"Update At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "LITERAL",
        key: "key.keyword",
        label: LanguageUtil.get({id:"common.label.key", defaultMessage:"Key"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        key: `result.${user.activeRiskType}.metadata.code.keyword`,
        label: LanguageUtil.get({id:"common.label.reason", defaultMessage:"Reason"}),
        properties: {dataType: DataType.STRING}
      }
    ];

    if (transactionName != undefined) {
      const transactionQuery = QueryBuilder.select()
        .from(CollectionNames.TRANSACTIONS)
        .where(eq("id", transactionName))
        .build();

      const transaction = await repository.find<TransactionJsonModel>(transactionQuery);
      if (transaction != undefined) {
        for (const v1 of transaction.variables
          .sort((a, b) => a.name.localeCompare(b.name))
          .filter(v => v.properties.dataType != DataType.STRUCTURE)) {
          await this.addVariable(v1, fields);
        }
      }
    }

    if (key) {
      const filters = page.filters;
      const existingFilter = filters[key];
      filters[key] = {
        filter: saveFilter && existingFilter != undefined ? existingFilter.filter : ValueFactory.initializeFilter(),
        items: fields
      }
      dispatch(changePageFilters(filters));
    } else {
      dispatch(changePageFilter({
        filter: saveFilter ? page.filter.filter : ValueFactory.initializeFilter(),
        items: fields
      }));
    }

  }

  public static async getMLOpsDatasourceFields(
    channel: string | undefined,
    baseFeatures: Array<{ name: string; properties: { dataType: DataType } }>,
    filterKey: string,
    saveFilter: boolean = true,
  ): Promise<void> {
    await this.getCaseFields(channel, saveFilter, filterKey);
    const page = store.getState().page;
    const user = store.getState().auth.user;
    if (user == undefined) {
      return;
    }
    const existingFilter = page.filters[filterKey];
    if (existingFilter == undefined) {
      return;
    }
    const allowedKeys = this.getMLOpsDatasourceAllowedFilterKeys(user.activeRiskType, baseFeatures);
    const filters = page.filters;
    filters[filterKey] = {
      filter: existingFilter.filter,
      items: existingFilter.items.filter((item: any) => allowedKeys.has(item.key)),
    };
    dispatch(changePageFilters(filters));
  }

  private static getMLOpsDatasourceAllowedFilterKeys(
    riskType: string,
    baseFeatures: Array<{ name: string; properties: { dataType: DataType } }>,
  ): Set<string> {
    const keys = new Set<string>([
      "id.keyword",
      `result.${riskType}.score`,
      `result.${riskType}.metadata.assignee.id.keyword`,
      `result.${riskType}.resolution.keyword`,
      `result.${riskType}.status.keyword`,
      "createdAt",
      `result.${riskType}.metadata.review.endDate`,
      `result.${riskType}.metadata.code.keyword`,
    ]);

    for (const feature of baseFeatures) {
      switch (feature.properties.dataType) {
        case DataType.STRING:
          keys.add(`variables.${feature.name}.keyword`);
          break;
        case DataType.NUMBER:
        case DataType.DATE:
        case DataType.BOOLEAN:
          keys.add(`variables.${feature.name}`);
          break;
        default:
          keys.add(`variables.${feature.name}`);
          break;
      }
    }

    return keys;
  }

  private static getAssessmentFields(): CustomFilterFields[] {
    return [
      {
        type: "LITERAL",
        key: "id",
        label: LanguageUtil.get({id:"common.label.id", defaultMessage:"ID"}),
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        key: "score",
        label: LanguageUtil.get({id:"common.label.score", defaultMessage:"Score"}),
        properties: {dataType: DataType.NUMBER}
      },

      {
        type: "DATE",
        key: "createdAt",
        label: LanguageUtil.get({id:"common.label.createdAt", defaultMessage:"Created At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: "lastUpdatedAt",
        label: LanguageUtil.get({id:"common.label.updatedAt", defaultMessage:"Updated At"}),
        properties: {dataType: DataType.DATE}
      }
    ];
  }

  private static async addVariable(v: TransactionVariableJsonModel, fields: CustomFilterFields[]) {
    switch (v.properties.dataType) {
      case DataType.BOOLEAN:
        fields.push({
          type: "CATEGORICAL",
          key: `variables.${v.name}`,
          label: (v.description && v.description.length > 0) ? v.description : v.name,
          properties: v.properties,
          items: [
            {
              label: LanguageUtil.get({id:"common.label.true", defaultMessage:"True"}),
              key: true
            },
            {
              label: LanguageUtil.get({id:"common.label.false", defaultMessage:"False"}),
              key: false,
            }
          ]
        });
        break;
      case DataType.DATE:
        fields.push({
          type: "DATE",
          key: `variables.${v.name}`,
          label: (v.description && v.description.length > 0) ? v.description : v.name,
          properties: v.properties
        });
        break;
      case DataType.ARRAY:
        await this.getArrayVariable(v, fields, v.name);
        break;
      default:
        fields.push({
          type: "LITERAL",
          key: `variables.${v.name}${v.properties.dataType == DataType.STRING ? ".keyword" : ""}`,
          label: (v.description && v.description.length > 0) ? v.description : v.name,
          properties: v.properties
        });
    }
  }

  private static async getArrayVariable(variable: TransactionVariableJsonModel, fields: CustomFilterFields[], _prefix: string): Promise<CustomFilterFields[]> {
    const structClient: StructClient = GatewayContainer.getInstance().get(GatewayNames.STRUCT_CLIENT);
    const structures = await structClient.getAll();
    const struct = structures.find(f => f.name == ((variable.properties as ArrayVariableProperties).properties as CustomStructureProperties).name)
    if (!struct) return [];

    const addStructVariables = async (struct: DataStructure, prefix: string) => {

      if (struct.properties?.dataType == DataType.OBJECT) {
        const variables = (struct.properties as ObjectVariableProperties).variables;
        variables.forEach(v => {
          switch (v.properties.dataType) {
            case DataType.ARRAY:
              this.getArrayVariable(v, fields, `${prefix}.${v.name}`)
              break;
            case DataType.STRUCTURE:
              const innerStruct = structures.find(f => f.name == (v.properties as CustomStructureProperties).name);
              if (innerStruct) {
                addStructVariables(innerStruct, `${prefix}.${v.name}`);
              }
              break;
            case DataType.BOOLEAN:
              fields.push({
                type: "CATEGORICAL",
                key: `variables.${prefix}.${v.name}`,
                label: `${prefix}.${v.name}`,
                properties: v.properties,
                items: [
                  {
                    label: LanguageUtil.get({id:"common.label.true", defaultMessage:"True"}),
                    key: true
                  },
                  {
                    label: LanguageUtil.get({id:"common.label.false", defaultMessage:"False"}),
                    key: false,
                  }
                ]
              });
              break;
            case DataType.DATE:
              fields.push({
                type: "DATE",
                key: `${prefix}.${v.name}`,
                label: `${prefix}.${v.name}`,
                properties: v.properties
              });
              break;
            default:
              fields.push({
                type: "LITERAL",
                key: `variables.${prefix}.${v.name}${v.properties.dataType == DataType.STRING ? ".keyword" : ""}`,
                label: `${prefix}.${v.name}`,
                properties: v.properties
              });
          }
        })
      }
    }

    await addStructVariables(struct, _prefix);
    return fields;
  }

  private static getTransactionFields(): CustomFilterFields[] {
    return [...this.getCommonFields(), this.getDeployStatus()];
  }

  private static async getStreamFields(): Promise<CustomFilterFields[]> {
    return [
      ...this.getCommonFields(),
      await this.getTransactionField("transaction"),
      this.getDeployStatus()
    ]
  }

  private static async getTrackerFields(): Promise<CustomFilterFields[]> {
    return [
      ...this.getCommonFields(),
      await this.getTransactionField("transaction"),
      this.getDeployStatus()
    ]
  }

  private static getListFields(): CustomFilterFields[] {
    return [
      ...this.getCommonFields(),
      {
        type: "CATEGORICAL",
        label: LanguageUtil.get({id:"common.label.type", defaultMessage:"Type"}),
        key: "type",
        properties: {
          dataType: DataType.STRING,
        },
        items: [
          {
            label:LanguageUtil.get({id:"common.label.externalList", defaultMessage:"External List"}),
            key: "EXTERNAL_LIST"
          },
          {
            label: LanguageUtil.get({id:"common.label.internalList", defaultMessage:"Internal List"}),
            key: "INTERNAL_LIST",
          }
        ]
      },
      this.getDeployStatus()
    ]
  }

  private static async getEnrichmentFields(): Promise<CustomFilterFields[]> {
    return [
      ...this.getCommonFields(),
      await this.getTransactionField("transaction"),
      {
        type: "CATEGORICAL",
        label: LanguageUtil.get({id:"common.label.type", defaultMessage:"Type"}),
        key: "type",
        properties: {
          dataType: DataType.STRING,
        },
        items: [
          {
            label: LanguageUtil.get({id:"common.label.velocity", defaultMessage:"Velocity"}),
            key: VariableType.VELOCITY
          },
          {
            label: LanguageUtil.get({id:"common.label.list", defaultMessage:"List"}),
            key: VariableType.LIST
          },
          {
            label: LanguageUtil.get({id:"common.label.query", defaultMessage:"Query"}),
            key: VariableType.QUERY
          },
          {
            label: LanguageUtil.get({id:"common.label.basic", defaultMessage:"Basic"}),
            key: VariableType.BASIC
          },
          {
            label: LanguageUtil.get({id:"common.label.api", defaultMessage:"API"}),
            key: VariableType.API
          }
        ]
      },
      this.getDeployStatus()
    ]
  }

  private static getCommonFields(): CustomFilterFields[] {
    return [
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.name", defaultMessage:"Name"}),
        key: "name",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.description", defaultMessage:"Description"}),
        key: "description",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "DATE",
        key: "createdAt",
        label: LanguageUtil.get({id:"common.label.createdAt", defaultMessage:"Created At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: "updatedAt",
        label: LanguageUtil.get({id:"common.label.updatedAt", defaultMessage:"Updated At"}),
        properties: {dataType: DataType.DATE}
      }
    ]
  }

  private static async getTransactionField(key: string): Promise<CustomFilterFields> {
    const transactions = await this.getTransactionNames();
    return {
      type: "CATEGORICAL",
      label: LanguageUtil.get({id: "common.label.channel", defaultMessage: "Channel"}),
      key: key,
      properties: {dataType: DataType.STRING},
      items: transactions.map(transaction => {
        return {
          label: transaction,
          key: transaction
        }
      })
    }
  }

  private static async getRuleFields(): Promise<CustomFilterFields[]> {
    return [
      ...this.getCommonFields(),
      await this.getTransactionField("workflow.children.value.name"),
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.priority", defaultMessage:"Priority"}),
        key: "priority",
        properties: {dataType: DataType.NUMBER}
      },
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.score", defaultMessage:"Score"}),
        key: "score",
        properties: {dataType: DataType.NUMBER},
        emptyMeansNone: true
      },
      this.getDeployStatus()
    ];
  }

  private static async getConnectionFields(): Promise<CustomFilterFields[]> {
    return [
      ...this.getCommonFields(),
      {
        type: "CATEGORICAL",
        label: LanguageUtil.get({id:"common.label.type", defaultMessage:"Type"}),
        key: "databaseType",
        properties: {dataType: DataType.STRING},
        items: ["PostgreSQL", "MySQL", "Mongo", "Oracle Database", "Microsoft SQL Server"].map(
          type => {
            return {
              label: type,
              key: type
            }
          }
        )
      },
      this.getDeployStatus()
    ]
  }

  private static async getStructFields() {
    return [
      ...this.getCommonFields(),
    ];
  }

  private static getTransactionNames(): Promise<string[]> {
    const transactionClient: TransactionClient = GatewayContainer.getInstance().get(GatewayNames.TRANSACTION_CLIENT);
    return transactionClient.getNames();
  }

  private static async getActionFields(): Promise<CustomFilterFields[]> {
    return [
      ...this.getCommonFields(),
      await this.getTransactionField("transaction"),
      {
        type: "CATEGORICAL",
        label: LanguageUtil.get({id:"common.label.type", defaultMessage:"Type"}),
        key: "template",
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.email", defaultMessage:"Email"}),
            key: "email"
          },
          {
            label: LanguageUtil.get({id:"common.label.teams", defaultMessage:"Microsoft Teams"}),
            key: "microsoft-teams"
          },
          {
            label: LanguageUtil.get({id:"common.label.api", defaultMessage:"API"}),
            key: "api"
          },
          {
            label: LanguageUtil.get({id:"common.label.slack", defaultMessage:"Slack"}),
            key: "slack"
          },
          {
            label: LanguageUtil.get({id:"common.label.discord", defaultMessage:"Discord"}),
            key: "discord"
          },
          {
            label: LanguageUtil.get({id:"common.label.jira", defaultMessage:"Jira"}),
            key: "jira"
          }
        ]
      },
      this.getDeployStatus()
    ];
  }

  private static getDeployStatus(): CustomFilterFields {
    return {
      type: "CATEGORICAL",
      key: "stage",
      label: LanguageUtil.get({id:"common.label.status", defaultMessage:"Status"}),
      properties: {dataType: DataType.STRING},
      items: [
        {
          label: LanguageUtil.get({id:"common.label.draft", defaultMessage:"Draft"}),
          key: "DRAFT" as DeploymentStatus
        },
        {
          label: LanguageUtil.get({id:"common.label.active", defaultMessage:"Active"}),
          key: "ACTIVE" as DeploymentStatus
        },
        {
          label: LanguageUtil.get({id:"common.label.scheduled", defaultMessage:"Scheduled"}),
          key: "SCHEDULED" as DeploymentStatus
        },
        {
          label: LanguageUtil.get({id:"common.label.inactive", defaultMessage:"Inactive"}),
          key: "INACTIVE" as DeploymentStatus
        },
        {
          label: LanguageUtil.get({id:"common.label.expired", defaultMessage:"Expired"}),
          key: "EXPIRED" as DeploymentStatus
        },
        {
          label: LanguageUtil.get({id:"common.label.failed", defaultMessage:"Failed"}),
          key: "FAILED" as DeploymentStatus
        },
        {
          label: LanguageUtil.get({id:"common.label.restoring", defaultMessage:"Restoring"}),
          key: "RESTORING" as DeploymentStatus
        },
        {
          label: LanguageUtil.get({id:"common.label.rebalancing", defaultMessage:"Rebalancing"}),
          key: "REBALANCING" as DeploymentStatus
        }
      ]
    }
  }

  private static async getUserFields(): Promise<CustomFilterFields[]> {
    const roleClient: RoleClient = GatewayContainer.getInstance().get(GatewayNames.ROLE_CLIENT);
    const groupClient: GroupClient = GatewayContainer.getInstance().get(GatewayNames.GROUP_CLIENT);
    const roles: string[] = await roleClient.getRoleNames();
    const groups: string[] = ["Admin", ...await groupClient.getGroupNames()];
    return [
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.username", defaultMessage:"Username"}),
        key: "username",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.firstName", defaultMessage:"First Name"}),
        key: "firstName",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.lastName", defaultMessage:"Last Name"}),
        key: "lastName",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.email", defaultMessage:"Email"}),
        key: "email",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: `roles`,
        label: LanguageUtil.get({id:"common.label.roles", defaultMessage:"Roles"}),
        properties: {dataType: DataType.STRING},
        items: roles.map((role) => ({
          label: role,
          key: role
        }))
      },
      {
        type: "CATEGORICAL",
        key: `groups`,
        label: LanguageUtil.get({id:"common.label.groups", defaultMessage:"Groups"}),
        properties: {dataType: DataType.STRING},
        items: groups.map((group) => ({
          label: group,
          key: group
        }))
      },
      {
        type: "CATEGORICAL",
        key: `riskType`,
        label: LanguageUtil.get({id:"common.label.type", defaultMessage:"Type"}),
        properties: {dataType: DataType.STRING},
        items: [
          {
            label: LanguageUtil.get({id:"common.label.fraud", defaultMessage:"Fraud"}),
            key: "fraud"
          },
          {
            label: LanguageUtil.get({id:"common.label.compliance", defaultMessage:"Compliance"}),
            key: "compliance"
          },
        ]
      },
      {
        type: "DATE",
        key: "createdAt",
        label: LanguageUtil.get({id:"common.label.createdAt", defaultMessage:"Created At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: "updatedAt",
        label: LanguageUtil.get({id:"common.label.updatedAt", defaultMessage:"Updated At"}),
        properties: {dataType: DataType.DATE}
      }
    ]
  }

  private static async getGroupFields(): Promise<CustomFilterFields[]> {
    const roleClient: RoleClient = GatewayContainer.getInstance().get(GatewayNames.ROLE_CLIENT);
    const roles: string[] = await roleClient.getRoleNames();
    return [
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.name", defaultMessage:"Name"}),
        key: "name",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.description", defaultMessage:"Description"}),
        key: "description",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "CATEGORICAL",
        key: `roles`,
        label: LanguageUtil.get({id:"common.label.roles", defaultMessage:"Roles"}),
        properties: {dataType: DataType.STRING},
        items: roles.map((role) => ({
          label: role,
          key: role
        }))
      },
      {
        type: "DATE",
        key: "createdAt",
        label: LanguageUtil.get({id:"common.label.createdAt", defaultMessage:"Created At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: "updatedAt",
        label: LanguageUtil.get({id:"common.label.updatedAt", defaultMessage:"Updated At"}),
        properties: {dataType: DataType.DATE}
      }
    ]
  }

  private static getRoleFields(): CustomFilterFields[] {
    return [
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.name", defaultMessage:"Name"}),
        key: "name",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "LITERAL",
        label: LanguageUtil.get({id:"common.label.description", defaultMessage:"Description"}),
        key: "description",
        properties: {dataType: DataType.STRING}
      },
      {
        type: "DATE",
        key: "createdAt",
        label: LanguageUtil.get({id:"common.label.createdAt", defaultMessage:"Created At"}),
        properties: {dataType: DataType.DATE}
      },
      {
        type: "DATE",
        key: "updatedAt",
        label: LanguageUtil.get({id:"common.label.updatedAt", defaultMessage:"Updated At"}),
        properties: {dataType: DataType.DATE}
      }
    ]
  }

  private static async getConfigurationFields() {
    return [
      ...this.getCommonFields(),
      await this.getTransactionField("dataSource")
    ];
  }

  public static async getListDataFields(fields: ListColumnField[], saveFilter: boolean = false) {
    const page = store.getState().page;
    const filterFields = fields.map((field) => {
      const fieldType = field.type === DataType.DATE ? 'DATE' : 'LITERAL';

      return {
        type: fieldType,
        key: field.name,
        label: LanguageUtil.get({id:`common.label.${field.name}`, defaultMessage: field.name }),
        properties: { dataType: field.type },
      } as CustomFilterFields
    })
    dispatch(changePageFilter({
      filter: saveFilter ? page.filter.filter : ValueFactory.initializeFilter(),
      items: filterFields
    }));
  }

  public static parse(filter: CustomFilter): Filter {
    const filters: Filter[] = filter.fields
      .filter(f => f.enabled)
      .map(f => {
        switch (f.search.type) {
          case "LITERAL":
            return this.parseLiteralFilter(f, f.search.value as CustomFilterLiteralSearch);
          case "CATEGORICAL":
            return this.parseCategoricalFilter(f, f.search.value as CustomFilterCategorical)
          case "DATE":
            return this.parseDateFilter(f, f.search.value as CustomFilterDateSearch)
        }
      })
      .filter(f => f != undefined) as Filter[];
    if (filters.length == 0) return empty();
    switch (filter.type) {
      case "ALL_MATCH":
        return and(...filters);
      case "ANY_MATCH":
        return or(...filters);
      case "NONE_MATCH":
        return nor(...filters)
    }
    return empty();
  }

  private static parseLiteralFilter = (field: CustomFilterField, filter: CustomFilterLiteralSearch) => {
    switch (filter.properties.dataType) {
      case DataType.STRING:
        return this.parseStringLiteral(field, filter);
      case DataType.NUMBER:
        return this.parseNumberLiteral(field, filter);
    }
  }

  private static isEmptyFilterValue = (value: unknown): boolean => {
    return value === undefined || value === null || value === "";
  }

  private static parseNumberLiteral = (field: CustomFilterField, filter: CustomFilterLiteralSearch) => {
    switch (filter.operator) {
      case "eq":
        if (field.emptyMeansNone && this.isEmptyFilterValue(filter.value)) {
          return or(exists(field.field, false), eq(field.field, null));
        }
        return eq(field.field, filter.value)
      case "neq":
        if (field.emptyMeansNone && this.isEmptyFilterValue(filter.value)) {
          return exists(field.field, true);
        }
        return neq(field.field, filter.value)
      case "gt":
        return gt(field.field, filter.value)
      case "gte":
        return gte(field.field, filter.value)
      case "lt":
        return lt(field.field, filter.value)
      case "lte":
        return lte(field.field, filter.value);
      default:
        console.error("Unsupported number filter type");
        return empty()
    }
  }

  private static parseStringLiteral = (field: CustomFilterField, filter: CustomFilterLiteralSearch) => {
    switch (filter.operator) {
      case "eq":
        if (this.isEmptyFilterValue(filter.value)) {
          return or(eq(field.field, ""), exists(field.field, false));
        }
        return eq(field.field, filter.value)
      case "neq":
        if (this.isEmptyFilterValue(filter.value)) {
          return and(exists(field.field, true), neq(field.field, ""));
        }
        return neq(field.field, filter.value)
      case "contains":
        if (this.isEmptyFilterValue(filter.value)) return empty();
        return includes(field.field, (filter.value as string))
      case "startsWith":
        if (this.isEmptyFilterValue(filter.value)) return empty();
        return starts(field.field, (filter.value as string))
      case "endsWith":
        if (this.isEmptyFilterValue(filter.value)) return empty();
        return ends(field.field, (filter.value as string))
      case "regex":
        if (this.isEmptyFilterValue(filter.value)) return empty();
        try {
          new RegExp(filter.value as string);
        } catch {
          return empty();
        }
        return regex(field.field, filter.value as string)
      case "notRegex":
        if (this.isEmptyFilterValue(filter.value)) return empty();
        try {
          new RegExp(filter.value as string);
        } catch {
          return empty();
        }
        return nor(regex(field.field, filter.value as string))
      default:
        console.error("Unsupported string filter type");
        return empty()
    }
  }

  private static parseCategoricalFilter(f: CustomFilterField, value: CustomFilterCategorical) {
    if (value.values.length == 0) return empty();
    return or(...value.values.flatMap(v => {
      if (f.search.subType == "EXIST" && typeof v == "boolean") {
        return [exists(f.field, v as boolean)];
      }
      if (f.field == "operation.keyword" && v == AuditLogType.EXPORT) {
        return [AuditLogType.EXPORT, ...this.exportAuditLogTypes].map(type => eq(f.field, type));
      }
      return [eq(f.field, v)];
    }))
  }

  private static parseDateFilter(f: CustomFilterField, value: CustomFilterDateSearch) {
    const startDate = value.startDate;
    const endDate = value.endDate;
    if (startDate == undefined || endDate == undefined) return empty();
    const start: number = moment(startDate.value).valueOf();
    let end: number = moment(endDate.value).valueOf();
    if (endDate.type == "Now") {
      end = moment().valueOf();
    }
    return and(gte(f.field, start), lte(f.field, end))
  }
}

export default FilterUtil;
